/**
 * PLATFORM'S PLAN CARD AND BILLING DRAWER (CC-05 §2, loader API in CC-01 §3).
 *
 * Voice draws nothing about the plan: loader.js (served by Platform's API host for
 * this environment) defines `window.SKBilling`, whose card and drawer are
 * Platform's iframes. Voice only hands them a 5-minute embed token, which Platform
 * mints from the signed-in user's own session cookie.
 */
import axiosInstance from '../api/axiosInstance';
import { PLATFORM_API } from '../api/endpoints';

export type BillingTrigger =
  | { kind: 'limit_hit'; unitCode: string }
  | { kind: 'feature_locked'; featureCode: string }
  | { kind: 'manual' };

interface BillingOpts {
  product: string;
  host: string;
  getToken: () => Promise<string>;
}

interface SKBillingApi {
  mountCard(el: HTMLElement, opts: BillingOpts): void;
  openDrawer(opts: BillingOpts, intent: 'upgrade' | 'topup', trigger?: BillingTrigger): void;
}

type BillingWindow = Window & { SKBilling?: SKBillingApi };

/** This product's code in Platform's catalog. */
const PRODUCT = 'voice';

let loading: Promise<SKBillingApi> | null = null;

function loadSkBilling(): Promise<SKBillingApi> {
  const w = window as BillingWindow;
  if (w.SKBilling) return Promise.resolve(w.SKBilling);
  if (!loading) {
    loading = new Promise<SKBillingApi>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `${PLATFORM_API}/embed/loader.js`;
      s.async = true;
      s.onload = () => (w.SKBilling ? resolve(w.SKBilling) : reject(new Error('loader.js defined no SKBilling')));
      s.onerror = () => reject(new Error('loader.js could not be loaded'));
      document.head.appendChild(s);
    }).catch((err: unknown) => {
      loading = null; // a later click may retry
      throw err;
    });
    // A finished payment changes what the org may use: start fresh from the server.
    window.addEventListener('sk-billing:completed', () => window.location.reload());
  }
  return loading;
}

const opts = (): BillingOpts => ({
  product: PRODUCT,
  host: PLATFORM_API,
  getToken: async () =>
    (await axiosInstance.post<{ data: { token: string } }>(`${PLATFORM_API}/v1/embed-tokens`, { productCode: PRODUCT })).data.data.token,
});

export async function mountPlanCard(el: HTMLElement): Promise<void> {
  (await loadSkBilling()).mountCard(el, opts());
}

export async function openUpgrade(trigger: BillingTrigger = { kind: 'manual' }): Promise<void> {
  (await loadSkBilling()).openDrawer(opts(), 'upgrade', trigger);
}

export async function openTopUp(trigger: BillingTrigger = { kind: 'manual' }): Promise<void> {
  (await loadSkBilling()).openDrawer(opts(), 'topup', trigger);
}
